import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import {Textarea} from '../ui/textarea';
import {Label} from '../ui/label';
import {FileText, Check} from 'lucide-react';

const MedicalHistorySection = ({formData, setFormData}) => {
  const medicalProblemsOptions = [
    'Artificial Valves / Pacemakers',
    'Asthma',
    'Allergy',
    'Bleeding Disorder',
    'Epilepsy / Seizure',
    'Heart Disease',
    'Hypertension / Hypotension',
    'Hormone Disorder',
    'Jaundice / Liver Disease',
    'Stomach Ulcer',
    'Low / High Pressure',
    'Arthritis / Joint Problem',
    'Kidney Problems',
    'Thyroid Problems',
    'Others'
  ];

  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProblemToggle = (problem) => {
    setFormData(prev => {
      const problems = prev.medical_problems || [];
      if (problems.includes(problem)) {
        return { ...prev, medical_problems: problems.filter(p => p !== problem) };
      } else {
        return { ...prev, medical_problems: [...problems, problem] };
      }
    });
  };

  return (
    <Card className="glass-effect border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-400" />
          Medical History
        </CardTitle>
        <CardDescription className="text-gray-300">
          Please provide your complete medical history for safe treatment planning.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Question 1: Surgery or hospitalized */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            1. Surgery or hospitalized in the past 5 years?
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="surgery_hospitalized"
                value="Yes"
                checked={formData.surgery_hospitalized === 'Yes'}
                onChange={(e) => handleRadioChange('surgery_hospitalized', e.target.value)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="surgery_hospitalized"
                value="No"
                checked={formData.surgery_hospitalized === 'No'}
                onChange={(e) => {
                  handleRadioChange('surgery_hospitalized', e.target.value);
                  setFormData(prev => ({ ...prev, surgery_details: '' }));
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">No</span>
            </label>
          </div>
          {formData.surgery_hospitalized === 'Yes' && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Procedure details *</Label>
              <Textarea
                value={formData.surgery_details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, surgery_details: e.target.value }))}
                placeholder="Specify procedure, month/year, and reason"
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Question 2: Fever/cold/cough */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            2. Are you suffering from fever/cold/cough?
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="fever_cold_cough"
                value="Yes"
                checked={formData.fever_cold_cough === 'Yes'}
                onChange={(e) => handleRadioChange('fever_cold_cough', e.target.value)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="fever_cold_cough"
                value="No"
                checked={formData.fever_cold_cough === 'No'}
                onChange={(e) => {
                  handleRadioChange('fever_cold_cough', e.target.value);
                  setFormData(prev => ({ ...prev, symptoms_details: '' }));
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">No</span>
            </label>
          </div>
          {formData.fever_cold_cough === 'Yes' && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Symptoms details *</Label>
              <Textarea
                value={formData.symptoms_details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, symptoms_details: e.target.value }))}
                placeholder="Please describe your symptoms"
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Question 3: Medical problems */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            3. Do you have any of the following problems? (select all that apply)
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {medicalProblemsOptions.map((problem) => (
              <button
                key={problem}
                type="button"
                onClick={() => handleProblemToggle(problem)}
                className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  (formData.medical_problems || []).includes(problem)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                }`}
              >
                {(formData.medical_problems || []).includes(problem) && (
                  <Check className="h-3 w-3 inline mr-1" />
                )}
                {problem}
              </button>
            ))}
          </div>
        </div>

        {/* Question 4: Abnormal bleeding */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            4. Any history of abnormal bleeding following injury or operation?
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="abnormal_bleeding"
                value="Yes"
                checked={formData.abnormal_bleeding === 'Yes'}
                onChange={(e) => handleRadioChange('abnormal_bleeding', e.target.value)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="abnormal_bleeding"
                value="No"
                checked={formData.abnormal_bleeding === 'No'}
                onChange={(e) => {
                  handleRadioChange('abnormal_bleeding', e.target.value);
                  setFormData(prev => ({ ...prev, abnormal_bleeding_details: '' }));
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">No</span>
            </label>
          </div>
          {formData.abnormal_bleeding === 'Yes' && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Please provide details *</Label>
              <Textarea
                value={formData.abnormal_bleeding_details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, abnormal_bleeding_details: e.target.value }))}
                placeholder="Please provide details about abnormal bleeding"
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Question 5: Taking medicine */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            5. Are you taking any medicine?
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="taking_medicine"
                value="Yes"
                checked={formData.taking_medicine === 'Yes'}
                onChange={(e) => handleRadioChange('taking_medicine', e.target.value)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="taking_medicine"
                value="No"
                checked={formData.taking_medicine === 'No'}
                onChange={(e) => {
                  handleRadioChange('taking_medicine', e.target.value);
                  setFormData(prev => ({ ...prev, medicine_details: '' }));
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">No</span>
            </label>
          </div>
          {formData.taking_medicine === 'Yes' && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Please provide details *</Label>
              <Textarea
                value={formData.medicine_details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, medicine_details: e.target.value }))}
                placeholder="Specify medicines you are taking"
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Question 6: Allergic to medication */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            6. Allergic to any medication?
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="allergic_medication"
                value="Yes"
                checked={formData.allergic_medication === 'Yes'}
                onChange={(e) => handleRadioChange('allergic_medication', e.target.value)}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">Yes</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="allergic_medication"
                value="No"
                checked={formData.allergic_medication === 'No'}
                onChange={(e) => {
                  handleRadioChange('allergic_medication', e.target.value);
                  setFormData(prev => ({ ...prev, allergy_details: '' }));
                }}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
              />
              <span className="text-white">No</span>
            </label>
          </div>
          {formData.allergic_medication === 'Yes' && (
            <div className="space-y-2">
              <Label className="text-white text-sm">Please provide details *</Label>
              <Textarea
                value={formData.allergy_details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, allergy_details: e.target.value }))}
                placeholder="Specify medications you are allergic to"
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Question 7: Past dental history */}
        <div className="space-y-3">
          <Label className="text-white text-sm sm:text-base">
            7. Past dental history
          </Label>
          <Textarea
            value={formData.past_dental_history || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 500) {
                setFormData(prev => ({ ...prev, past_dental_history: value }));
              }
            }}
            placeholder="Previous treatments, frequency of visits, any complications"
            className="bg-gray-800 border-gray-600 text-white"
            rows={5}
          />
          <p className="text-gray-400 text-xs sm:text-sm text-right">
            {(formData.past_dental_history || '').length}/500
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicalHistorySection;
